// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ApiUser } from '../../types/accounts';
import { LeaderboardRecord } from '../../types/leaderboards';
import { consoleService } from '../../services/consoleService';
import { authService } from '../../services/authService';
import { UserRole } from '../../types/auth';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import ConfirmDialog from '../ConfirmDialog';

interface LeaderboardTabProps {
  account: ApiUser;
}

// Backend leaderboard IDs
const LEADERBOARD_IDS = [
  { id: 'total_earned_coins_global', name: 'Global', icon: '🌍' },
  { id: 'total_earned_coins_ludo', name: 'Ludo', icon: '🎲' },
  { id: 'total_earned_coins_quiz', name: 'Quiz', icon: '🧠' },
  { id: 'total_earned_coins_solitaire', name: 'Solitaire', icon: '🃏' },
];

interface UserLeaderboardRecord {
  leaderboardId: string;
  leaderboardName: string;
  leaderboardIcon: string;
  rank: number | null;
  score: string;
  updateTime: string;
  ownerId: string;
}

const LeaderboardTab: React.FC<LeaderboardTabProps> = ({ account }) => {
  const [userRecords, setUserRecords] = useState<UserLeaderboardRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const { dialogState, openConfirmDialog, closeConfirmDialog, handleConfirm } = useConfirmDialog();
  const sessionRole = authService.getSessionRole();

  const canDelete = () => sessionRole === UserRole.USER_ROLE_ADMIN;

  const getAuthHeaders = () => {
    const session = authService.getSession();
    return {
      Authorization: `Bearer ${session?.token}`
    };
  };

  const apiHost = process.env.REACT_APP_API_HOST || 'http://localhost:7351';

  useEffect(() => {
    loadLeaderboardRecords();
  }, [account.id]);

  const loadLeaderboardRecords = async () => {
    try {
      setLoading(true);
      setError('');
      
      const results: UserLeaderboardRecord[] = [];

      // Fetch records from each leaderboard using the ListLeaderboardRecords endpoint
      for (const lb of LEADERBOARD_IDS) {
        try {
          // Use the ListLeaderboardRecords API endpoint which returns player names
          const response: any = await axios.post(
            `${apiHost}/v2/console/api/endpoints/ListLeaderboardRecords`,
            {
              body: JSON.stringify({ leaderboardId: lb.id, limit: 1000 }),
              user_id: '00000000-0000-0000-0000-000000000000',
              session_vars: {}
            },
            { headers: getAuthHeaders() }
          );
          
          // Response has body as JSON string - need to parse it
          const responseBody = typeof response.data.body === 'string' 
            ? JSON.parse(response.data.body) 
            : response.data;
          
          const allRecords: LeaderboardRecord[] = responseBody.records || [];
          
          // Find the user's record in this leaderboard (API returns ownerId in camelCase)
          const userRecord = allRecords.find((r: any) => r.ownerId === account.id || r.owner_id === account.id);
          
          if (userRecord) {
            // API returns camelCase fields (ownerId, updateTime) not snake_case
            const record = userRecord as any;
            results.push({
              leaderboardId: lb.id,
              leaderboardName: lb.name,
              leaderboardIcon: lb.icon,
              rank: record.rank ? parseInt(record.rank) : null,
              score: record.score,
              updateTime: record.updateTime || record.update_time,
              ownerId: record.ownerId || record.owner_id,
            });
          } else {
            // No record for this user on this leaderboard
            results.push({
              leaderboardId: lb.id,
              leaderboardName: lb.name,
              leaderboardIcon: lb.icon,
              rank: null,
              score: '0',
              updateTime: '',
              ownerId: account.id,
            });
          }
        } catch (err) {
          console.warn(`Failed to fetch records for ${lb.id}:`, err);
          // Add empty record on error
          results.push({
            leaderboardId: lb.id,
            leaderboardName: lb.name,
            leaderboardIcon: lb.icon,
            rank: null,
            score: '0',
            updateTime: '',
            ownerId: account.id,
          });
        }
      }

      setUserRecords(results);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load leaderboard records');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRecord = (record: UserLeaderboardRecord) => {
    openConfirmDialog(
      'Delete Leaderboard Record',
      `Are you sure you want to delete this record from "${record.leaderboardName}"?`,
      async () => {
        try {
          await consoleService.deleteLeaderboardRecord('', record.leaderboardId, record.ownerId);
          // Reload records after deletion
          await loadLeaderboardRecords();
        } catch (err: any) {
          setError(err.response?.data?.message || err.message || 'Failed to delete leaderboard record');
        }
      }
    );
  };

  const formatScore = (score: string) => {
    const parsedScore = parseFloat(score);
    if (!isNaN(parsedScore)) {
      return parsedScore.toLocaleString();
    }
    return score;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="leaderboard-tab">
        <div className="alert alert-info">
          <div className="spinner-border spinner-border-sm me-2" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <strong>Loading leaderboard records...</strong>
        </div>
      </div>
    );
  }

  return (
    <div className="leaderboard-tab">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">🏆 Leaderboard Records</h4>
      </div>

      {error && (
        <div className="alert alert-danger mb-3">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Records Cards */}
      <div className="leaderboard-cards">
        {userRecords.map((record) => (
          <div key={record.leaderboardId} className="leaderboard-card">
            <div className="leaderboard-card-header">
              <span className="leaderboard-icon">{record.leaderboardIcon}</span>
              <h5>{record.leaderboardName}</h5>
            </div>
            <div className="leaderboard-card-body">
              {record.rank !== null ? (
                <>
                  <div className="stat-row">
                    <span className="stat-label">Rank:</span>
                    <span className="stat-value rank">#{record.rank}</span>
                  </div>
                  <div className="stat-row">
                    <span className="stat-label">Score:</span>
                    <span className="stat-value score">{formatScore(record.score)}</span>
                  </div>
                  <div className="stat-row">
                    <span className="stat-label">Last Updated:</span>
                    <span className="stat-value date">{formatDate(record.updateTime)}</span>
                  </div>
                  {canDelete() && (
                    <div className="leaderboard-card-actions">
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDeleteRecord(record)}
                      >
                        🗑️ Delete Record
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="no-record">
                  <span>No record</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <ConfirmDialog
        isOpen={dialogState.isOpen}
        title={dialogState.title}
        message={dialogState.message}
        onConfirm={handleConfirm}
        onCancel={closeConfirmDialog}
      />

      <style>{`
        .leaderboard-cards {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1rem;
        }
        
        .leaderboard-card {
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
        }
        
        .leaderboard-card-header {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 1rem;
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          border-bottom: 1px solid #e5e7eb;
        }
        
        .leaderboard-icon {
          font-size: 1.5rem;
        }
        
        .leaderboard-card-header h5 {
          margin: 0;
          font-size: 1rem;
          font-weight: 600;
          color: #1f2937;
        }
        
        .leaderboard-card-body {
          padding: 1rem;
        }
        
        .stat-row {
          display: flex;
          justify-content: space-between;
          padding: 0.5rem 0;
          border-bottom: 1px solid #f3f4f6;
        }
        
        .stat-row:last-of-type {
          border-bottom: none;
        }
        
        .stat-label {
          color: #6b7280;
          font-size: 0.875rem;
        }
        
        .stat-value {
          font-weight: 600;
          color: #1f2937;
        }
        
        .stat-value.score {
          color: #059669;
          font-size: 1.1rem;
        }
        
        .stat-value.rank {
          color: #f59e0b;
        }
        
        .stat-value.date {
          font-size: 0.75rem;
          color: #9ca3af;
        }
        
        .no-record {
          text-align: center;
          padding: 2rem 1rem;
          color: #9ca3af;
        }
        
        .leaderboard-card-actions {
          margin-top: 1rem;
          padding-top: 1rem;
          border-top: 1px solid #f3f4f6;
        }
      `}</style>
    </div>
  );
};

export default LeaderboardTab;
